<script lang="ts" setup>

import type {
    CommonButtonColorType,
    CommonButtonSizesType
} from '@/components/shared/form/Button.vue';
import { useEventModalsStore, useProviderConfig } from '#imports';
import useBuyNft from '~/composables/web3/useBuyNft.client';

const props = withDefaults(
    defineProps<{
        item: AppCartItemType;
        size?: CommonButtonSizesType;
        color?: CommonButtonColorType;
        buttonClass?: string | Record<string, boolean> | (string | Record<string, boolean>)[];
    }>(),
    {
        size: 'size.medium',
        color: 'bg.aviation',
        buttonClass: 'compact-600-accent'
    }
);

const { buyNFT, loadingState, AsyncState } = useBuyNft();

const isLoading = computed(() => loadingState.value[props.item.uuid] === AsyncState.IS_LOADING);

const { eventEmitter } = useEVMCore();

const {
    map: { chainMap },
    utils: { isEvmErc1155 }
} = useProviderConfig()(['map', 'utils']);

const modalEventsStore = useEventModalsStore();

const getDescriptionForModal = (itemType: EvmItemType) => {
    let result: string[];

    if (isEvmErc1155(itemType)) {
        result = [`${props.item.amount1155} items ${props.item.price?.amount} ${props.item.price?.symbol}`, `Chain: ${props.item.network}`];
    }
    else {
        result = [`${props.item.price?.amount} ${props.item.price?.symbol}`, `Chain: ${props.item.network}`];
    }

    return result;
};

const isSubscribed = ref(false);
const buttonKey = `BuyNowButton_${props.item.uuid}`;

onMounted(() => {
    eventEmitter.on('purchase', {
        key: buttonKey,
        onSuccess: () => {
            eventEmitter.off('purchase', buttonKey);
            isSubscribed.value = false;
        },
        onError: () => {
            modalEventsStore.add({
                type: 'error',
                status: 'error',
                images: [props.item?.displayImageURL ?? ''],
                imageState: 'error',
                title: props.item?.name,
                description: getDescriptionForModal(props.item.itemType),
                heading: 'Error',
                itemType: props.item?.itemType,
                content: 'Something went wrong, Try again later.'
            });
            eventEmitter.off('purchase', buttonKey);
            isSubscribed.value = false;
        },
        onLoad: (loading) => {
            if (loading) {
                modalEventsStore.add({
                    type: 'approve',
                    status: 'pending',
                    images: [props.item?.displayImageURL ?? ''],
                    imageState: 'loading',
                    title: props.item?.name,
                    description: getDescriptionForModal(props.item.itemType),
                    heading: 'Approve purchase',
                    itemType: 2,
                    content: 'You\'ll be asked to approve this purchase from your wallet'
                });
            }
        }
    });
});

onBeforeRouteLeave(() => {
    eventEmitter.off('purchase', buttonKey);
    isSubscribed.value = false;
    return true;
});

const handleClick = () => {
    isSubscribed.value = true;
};

onDeactivated(() => {
    if (!isSubscribed.value) {
        eventEmitter.off('purchase', buttonKey);
    }
});

onUnmounted(() => {
    if (!isSubscribed.value) {
        eventEmitter.off('purchase', buttonKey);
    }
});

</script>

<template>
  <Button v-bind="$attrs"
          :color="color"
          circle
          :size="size"
          :is-loading="isLoading"
          @click="handleClick"
  >
    <button type="button"
            :class="buttonClass"
            aria-label="buy now"
            @click.prevent="buyNFT([item], 'buyNow')"
    >
      <slot></slot>
    </button>
  </Button>
</template>

<style lang="scss">
.btn-purple {
  min-width: var(--control-700-size-single, 72px);
  padding-top: 12px;
  padding-bottom: 12px;
  transition: opacity 0.3s ease-in-out;
  border-radius: var(--radius-999, 999px);
  background: var(--bg-brand-aviation-primary);
  color: #FFF;

  &:hover {
    opacity: 0.2;
  }
}
</style>
